import { useEffect, useState } from "react";
import { ModalComponentV1 } from "Components/ModalComponentV1";
import { CustomSwitcher } from "Components/CustomSwitcher";
import { CustomInputNumber } from "Components/CustomInputNumber";
import { CustomTextAreaV2 } from "Components/CustomTextAreaV2";
import { CustomButton } from "Components/CustomButton";
import { RentPaymentTyp, ReturnDisputeType } from "helpers/types";
import warning from "assets/svg/Warning.svg";
import parentStyles from "../index.module.css";
import styles from "./index.module.css";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (blockAd: boolean) => void;
  loading: boolean;
  // значения формы Manage Dispute на момент нажатия Complete Dispute
  values: any;
  lessorRestriction: boolean;
  lesseeRestriction: boolean;
  stopProduct: boolean;
  returnType: ReturnDisputeType;
  insuranceType: RentPaymentTyp;
  onOpenReport: () => void;
  isOpenReportLoading: boolean;
};

const ReadonlySwitch = ({ checked, label }: { checked: boolean; label: string }) => (
  <div className={styles.switchItem}>
    <CustomSwitcher checked={checked} className={styles.readonly} tabIndex={-1} />
    <span className={styles.switchLabel}>{label}</span>
  </div>
);

const ReadonlyAmount = ({ label, value }: { label: string; value?: number }) => (
  <div className={styles.field}>
    <span className={styles.fieldLabel}>{label}</span>
    <CustomInputNumber value={value} suffix="€" readOnly tabIndex={-1} className={styles.readonlyInput} />
  </div>
);

export const CompleteDisputeModal = ({
  isOpen,
  onClose,
  onConfirm,
  loading,
  values,
  lessorRestriction,
  lesseeRestriction,
  stopProduct,
  returnType,
  insuranceType,
  onOpenReport,
  isOpenReportLoading,
}: Props) => {
  const isReturnImpossible = returnType === "RETURN_IMPOSSIBLE";
  const [blockAd, setBlockAd] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    // при Return Impossible объявление блокируется принудительно
    setBlockAd(isReturnImpossible || !!stopProduct);
  }, [isOpen, isReturnImpossible, stopProduct]);

  return (
    <ModalComponentV1
      title="Complete Dispute"
      className={`${parentStyles.modal} ${parentStyles.fiftyTwenty}`}
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className={styles.content}>
        <div className={styles.description}>Please confirm Dispute resolution with the following actions:</div>

        <div className={styles.sections}>
          <div className={styles.limitations}>
            <div className={styles.titleRow}>
              <img src={warning} alt="warning" width={22} height={22} />
              <span className={styles.title}>Limitations</span>
            </div>
            <div className={styles.switches}>
              <ReadonlySwitch checked={!!lessorRestriction} label="Lessor" />
              <ReadonlySwitch checked={!!lesseeRestriction} label="Renter" />
              <div className={styles.switchItem}>
                <CustomSwitcher
                  checked={blockAd}
                  onChange={setBlockAd}
                  disabled={isReturnImpossible}
                  className={isReturnImpossible ? styles.forced : undefined}
                />
                <span className={styles.switchLabel}>Block Ad</span>
              </div>
            </div>
          </div>

          <div className={styles.compensation}>
            <span className={styles.title}>Compensation</span>
            <div className={styles.columns}>
              <div className={styles.column}>
                <div className={styles.columnHeader}>
                  <span className={styles.title}>Renter</span>
                  <ReadonlySwitch checked={!!values?.renter?.responsible} label="Responsible" />
                </div>
                <div className={styles.fields}>
                  <ReadonlyAmount label="Compensation from Insurance" value={values?.renter?.compIns} />
                  <ReadonlyAmount label="Debt" value={values?.renter?.debt} />
                </div>
              </div>
              <div className={styles.column}>
                <div className={styles.columnHeader}>
                  <span className={styles.title}>Lessor</span>
                </div>
                <div className={styles.fields}>
                  <ReadonlyAmount
                    label={`Compensation from ${insuranceType === "DEPOSIT" ? "Deposit" : "Insurance"}`}
                    value={values?.lessor?.compDep}
                  />
                  <ReadonlyAmount label="Debt" value={values?.lessor?.debt} />
                </div>
              </div>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Public comment (will be shown in common Chat)</span>
              <CustomTextAreaV2
                value={values?.publicComment ?? ""}
                readOnly
                tabIndex={-1}
                className={styles.readonlyTextArea}
              />
            </div>
            <CustomButton
              sizeButton="large"
              typeButton="secondary"
              htmlType="button"
              loading={isOpenReportLoading}
              onClick={onOpenReport}
              className={styles.openReport}
            >
              Open Report
            </CustomButton>
          </div>
        </div>

        <div className={`${parentStyles.actionBtnsWrapper} ${styles.actions}`}>
          <CustomButton sizeButton="large" typeButton="tertiary" htmlType="button" onClick={onClose}>
            Cancel
          </CustomButton>
          <CustomButton
            sizeButton="large"
            typeButton="primary"
            htmlType="button"
            loading={loading}
            onClick={() => onConfirm(isReturnImpossible || blockAd)}
          >
            Complete Dispute
          </CustomButton>
        </div>
      </div>
    </ModalComponentV1>
  );
};
